/**
 * Theme state.
 *
 * Deliberately an *external store* read through `useSyncExternalStore`
 * rather than React context. Three reasons:
 *
 * 1. The chart is not a React component in any useful sense — it is a
 *    canvas instance that needs to be told "re-theme now". An external
 *    store can be subscribed to from an effect without a provider being an
 *    ancestor, and without a context re-render fanning out to every panel.
 * 2. `document.documentElement.dataset.theme` is written *synchronously*
 *    before subscribers are notified, so by the time anything reads
 *    `getComputedStyle` the new palette is already resolved. Doing this in
 *    a React effect would let a subscriber read the old palette.
 * 3. The boot script in `index.html` has already written the attribute
 *    before React exists. This module adopts that value rather than
 *    re-deciding it, so there is never a second source of truth.
 *
 * The toggle is three-state — Light / Dark / System — because a two-state
 * toggle gives you no way back to following the OS once you have touched
 * it. "System" is persisted as the *absence* of the key.
 */

import { useCallback, useSyncExternalStore, type ReactNode } from 'react';

import {
  CVD_STORAGE_KEY,
  THEME_STORAGE_KEY,
  currentTheme,
  readToken,
  systemTheme,
  type ThemeChoice,
  type ThemeName,
} from './tokens';

type Listener = () => void;

const listeners = new Set<Listener>();

function readStoredChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function readStoredCvd(): boolean {
  try {
    return localStorage.getItem(CVD_STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

let choice: ThemeChoice = readStoredChoice();
let resolved: ThemeName = currentTheme();
let cvd: boolean = readStoredCvd();

/**
 * The snapshot object. Cached and only replaced when something actually
 * changes: `useSyncExternalStore` compares by identity, and returning a
 * fresh object every call is an infinite render loop.
 */
export interface ThemeState {
  /** What the user picked. `system` means "follow the OS". */
  choice: ThemeChoice;
  /** What is actually painted right now. */
  theme: ThemeName;
  /** Colour-vision-deficiency palette active (green→blue, red→orange). */
  cvd: boolean;
  isDark: boolean;
}

let snapshot: ThemeState = {
  choice,
  theme: resolved,
  cvd,
  isDark: resolved === 'dark',
};

function publish(): void {
  snapshot = { choice, theme: resolved, cvd, isDark: resolved === 'dark' };
  for (const listener of listeners) listener();
}

let switchingTimer: number | null = null;

function paint(next: ThemeName, nextCvd: boolean): void {
  const root = document.documentElement;
  const themeChanged = root.dataset.theme !== next;
  const cvdChanged = (root.dataset.cvd === 'on') !== nextCvd;
  if (!themeChanged && !cvdChanged) return;

  // Kill transitions for one frame. Without this, `getComputedStyle` (how
  // the chart canvas gets its colours) samples a half-blended value and the
  // chart is painted in a colour that exists in neither palette.
  root.classList.add('theme-switching');

  root.dataset.theme = next;
  root.style.colorScheme = next;
  if (nextCvd) root.dataset.cvd = 'on';
  else delete root.dataset.cvd;

  // Read the literal back out of the stylesheet rather than repeating the
  // hex here. `data-theme` is already written above, so this resolves to the
  // new palette, and `theme-switching` has suppressed transitions so there
  // is no half-blended value to sample. Hardcoding it would be a fourth
  // copy of the background colour (index.html, both :root blocks) free to
  // drift out of step with the other three.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta instanceof HTMLMetaElement) {
    meta.content = readToken('--surface-0', next === 'dark' ? '#0e1116' : '#fbf9f6');
  }

  if (switchingTimer !== null) window.clearTimeout(switchingTimer);
  switchingTimer = window.setTimeout(() => {
    root.classList.remove('theme-switching');
    switchingTimer = null;
  }, 60);
}

function applyChoice(): void {
  const next: ThemeName = choice === 'system' ? systemTheme() : choice;
  const changed = next !== resolved;
  resolved = next;
  paint(next, cvd);
  if (changed || snapshot.choice !== choice) publish();
}

// Keep following the OS while the choice is `system`.
if (typeof window !== 'undefined' && window.matchMedia) {
  const media = window.matchMedia('(prefers-color-scheme: light)');
  const onChange = (): void => {
    if (choice === 'system') applyChoice();
  };
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', onChange);
  }
}

// Another tab changed the theme: follow it. A trader with the terminal on
// two monitors should not have one of them in the wrong palette.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === THEME_STORAGE_KEY) {
      choice = readStoredChoice();
      applyChoice();
    } else if (event.key === CVD_STORAGE_KEY) {
      cvd = readStoredCvd();
      paint(resolved, cvd);
      publish();
    }
  });
}

// Arrow-function properties, not methods: these are passed by reference
// into `useSyncExternalStore`, and a method detached from its object is a
// `this` bug waiting to happen.
export const themeStore = {
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot: (): ThemeState => snapshot,

  /** Set Light / Dark / System. */
  setChoice: (next: ThemeChoice): void => {
    choice = next;
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* Private mode. The in-memory choice still applies for this session. */
    }
    applyChoice();
  },

  /** Flip between light and dark, leaving `system` behind. */
  toggle: (): void => {
    themeStore.setChoice(resolved === 'dark' ? 'light' : 'dark');
  },

  setCvd: (enabled: boolean): void => {
    cvd = enabled;
    try {
      if (enabled) localStorage.setItem(CVD_STORAGE_KEY, 'on');
      else localStorage.removeItem(CVD_STORAGE_KEY);
    } catch {
      /* ignored */
    }
    paint(resolved, cvd);
    publish();
  },
};

// Adopt whatever the boot script painted, and apply the stored CVD flag
// (which the boot script deliberately does not handle — it only needs to
// prevent a flash, and CVD does not change the background).
if (typeof document !== 'undefined') {
  paint(resolved, cvd);
}

const SERVER_SNAPSHOT: ThemeState = {
  choice: 'system',
  theme: 'dark',
  cvd: false,
  isDark: true,
};

/** Subscribe to the whole theme state. */
export function useTheme(): ThemeState {
  return useSyncExternalStore(
    themeStore.subscribe,
    themeStore.getSnapshot,
    () => SERVER_SNAPSHOT,
  );
}

/**
 * Just the resolved theme name.
 *
 * A string primitive, so a component that only cares whether it is dark
 * does not re-render when the CVD flag changes.
 */
export function useThemeName(): ThemeName {
  return useSyncExternalStore(
    themeStore.subscribe,
    (): ThemeName => snapshot.theme,
    (): ThemeName => 'dark',
  );
}

export interface ThemeControls extends ThemeState {
  setChoice: (choice: ThemeChoice) => void;
  toggle: () => void;
  setCvd: (enabled: boolean) => void;
}

/** Theme state plus the setters, for the toggle component. */
export function useThemeControls(): ThemeControls {
  const state = useTheme();
  const setChoice = useCallback((next: ThemeChoice) => themeStore.setChoice(next), []);
  const toggle = useCallback(() => themeStore.toggle(), []);
  const setCvd = useCallback((enabled: boolean) => themeStore.setCvd(enabled), []);
  return { ...state, setChoice, toggle, setCvd };
}

/**
 * Provider component.
 *
 * The store works without it — it exists so the app has one obvious place
 * where "the theme is live" is declared, and so a future scoped-theme
 * island has somewhere to hang. It renders children unchanged.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  return children;
}

export type { ThemeChoice, ThemeName };
